import React from "react";
import ModalHeader from "./ModalHeader";
import ModalFooter from "./ModalFooter";
import ModalMain from "./ModalMain";
import { getProviderNameWithoutSpecialist } from "../../helpers/CommonUtility";
import { AuthRoutes } from "../../constants/Constants";

const AssignmentConflictModal = (props) => {
  const {
    showModal,
    handleConfirm,
    handleNotConfirm,
    title,
    confirmButtonText,
    cancelButtonText = "Cancel",
    conflicts = [],
  } = props;

  const PrimaryButtonList = [
    {
      title: confirmButtonText,
      onclick: handleConfirm,
    },
  ];
  const DefaultButtonList = [
    {
      title: cancelButtonText,
      onclick: handleNotConfirm,
    },
  ];

  const boldStyle = { fontWeight: 700 };

  const getConflictTypeLabel = (conflictType) => {
    switch ((conflictType || "").toUpperCase()) {
      case "REPORTED":
        return "Already Reported";
      case "EXTERNAL_FIRM_RULE_MISSING":
        return "External Firm Rule Missing";
      case "EXTERNAL_FIRM_ASSIGNED":
         return "Already Assigned to External Firm";
      default:
        return "Already Assigned";
  }
  };

  const renderSingleConflict = (conflict) => {
    const currentProviderName = conflict?.currentProviderName || conflict?.currentProviderId || "Unknown";
    const requestedProviderName = getProviderNameWithoutSpecialist(
      conflict?.requestedProviderName || conflict?.requestedProviderId || "Selected Radiologist"
    );

    const conflictType = (conflict?.conflictType || "").toUpperCase();

    if (conflictType === "REPORTED") {
      return (
        <p className="assignment-conflict-message">
          This case is already reported by <strong style={boldStyle}>{currentProviderName}</strong>. Do you want to reassign this case to <strong style={boldStyle}>{requestedProviderName}</strong>?
        </p>
      );
    }

    if (conflictType === "EXTERNAL_FIRM_RULE_MISSING") {
      return (
        <p className="assignment-conflict-message">
          This case does not match any assignment rule configured for{" "}<strong style={boldStyle}>{requestedProviderName}</strong>.
          <br />
          <br />
          Please review the latest external firm assignment rules in{" "}
          <a href={AuthRoutes.EXTERNAL_FIRM_CONFIG} target="_blank" rel="noopener noreferrer"  style={{color: "rgb(0, 120, 212)" }}> External Firm Configuration</a>{" "}before proceeding. Assigning a case that does not meet the firm's assignment requirements may result in delays, rejection, or manual reassignment later.
          <br />
          <br />
          Do you want to continue with the assignment?
        </p>
      );
    }

    if (conflictType === "EXTERNAL_FIRM_ASSIGNED") {
      return (
        <p className="assignment-conflict-message">
          This case is assigned to <strong style={boldStyle}>{currentProviderName}</strong> and has already been pushed for reading. The study cannot be automatically withdrawn or recalled.
          <br />
          <br />
          The external firm may already be reporting on this case. Reassigning it may result in duplicate reads.
          <br />
          <br />
          Please confirm before proceeding with the assignment to{" "}
          <strong style={boldStyle}>{requestedProviderName}</strong>.
        </p>
      );
    }
    return (
      <p className="assignment-conflict-message">
        This case is already assigned to <strong style={boldStyle}>{currentProviderName}</strong>. Do you want to reassign this case to <strong style={boldStyle}>{requestedProviderName}</strong>?
      </p>
    );
  };

  const renderBody = () => {
    if (!conflicts || conflicts.length === 0) {
      return null;
    }
    if (conflicts.length === 1) {
      return (
        <div
          className="assignment-conflict-modal-content"
          style={{ padding: "16px 20px" }}
        >
          <div
            className="assignment-conflict-message-container"
            style={{ fontSize: "14px", lineHeight: 1.6 }}
          >
            {renderSingleConflict(conflicts[0])}
          </div>
        </div>
      );
    }
    return (
      <div
        className="assignment-conflict-modal-content"
        style={{ padding: "12px 16px" }}
      >
        <p style={{ margin: "0 0 12px", fontSize: "14px", lineHeight: 1.5 }}>
          Assignment conflict detected. The studies below are currently assigned to another Provider.
        </p>
        <div
          className="table-container"
          style={{ maxHeight: "300px", overflowY: "auto", paddingBottom: "4px" }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              border: "1px solid #ddd",
            }}
          >
            <thead>
              <tr>
                <th
                  className="study-table-th"
                  style={{ border: "1px solid #ddd", padding: "8px" }}
                >
                  Patient Name
                </th>
                <th
                  className="study-table-th"
                  style={{ border: "1px solid #ddd", padding: "8px" }}
                >
                  Accession Number
                </th>
                <th
                  className="study-table-th"
                  style={{ border: "1px solid #ddd", padding: "8px" }}
                >
                  Conflict Type
                </th>
                <th
                  className="study-table-th"
                  style={{ border: "1px solid #ddd", padding: "8px" }}
                >
                  Current Provider
                </th>
                <th
                  className="study-table-th"
                  style={{ border: "1px solid #ddd", padding: "8px" }}
                >
                  Requested Provider
                </th>
              </tr>
            </thead>
            <tbody>
              {conflicts.map((conflict, idx) => (
                <tr key={idx} className="study-table-tr">
                  <td
                    className="study-table-td"
                    style={{ border: "1px solid #ddd", padding: "8px" }}
                  >
                    {conflict.patientName ?? "—"}
                  </td>
                  <td
                    className="study-table-td"
                    style={{ border: "1px solid #ddd", padding: "8px" }}
                  >
                    {conflict.accessionNumber ?? "—"}
                  </td>
                  <td
                    className="study-table-td"
                    style={{ border: "1px solid #ddd", padding: "8px" }}
                  >
                    {getConflictTypeLabel(conflict.conflictType)}
                  </td>
                  <td
                    className="study-table-td"
                    style={{ border: "1px solid #ddd", padding: "8px" }}
                  >
                    {conflict?.currentProviderName || "—"}
                  </td>
                  <td
                    className="study-table-td"
                    style={{ border: "1px solid #ddd", padding: "8px" }}
                  >
                    {getProviderNameWithoutSpecialist(conflict?.requestedProviderName) || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p
          className="assignment-conflict-summary"
          style={{ margin: "12px 0 0", fontSize: "13px" }}
        >
          Do you want to reassign the above studies?
        </p>
      </div>
    );
  };

  const modalDetails = {
    header: (
      <ModalHeader
        updatedModal={true}
        heading={title}
        handleCancel={handleNotConfirm}
      />
    ),
    footer: (
      <ModalFooter
        DefaultButtonList={DefaultButtonList}
        PrimaryButtonList={PrimaryButtonList}
      />
    ),
    body: (
      <div className="assignment-conflict-modal-body">
        {renderBody()}
      </div>
    ),
    width: conflicts && conflicts.length > 1 ? "900px" : "600px",
  };

  return (
    <>
      {showModal && (
        <ModalMain
          updatedModal={true}
          showModal={showModal}
          modalDetails={modalDetails}
        />
      )}
    </>
  );
};

export default AssignmentConflictModal;
